import React, { useState, useEffect, useRef } from 'react';
import { FiWifi, FiWifiOff, FiCloud, FiDatabase, FiRefreshCw, FiRotateCcw } from 'react-icons/fi';
import { checkConnection } from '../services/supabaseClient';
import toast, { Toaster } from 'react-hot-toast';

const ConnectionStatus = () => {
  const [status, setStatus] = useState({
    online: true,
    mode: 'supabase',
    checking: true
  });

  const [prevOnline, setPrevOnline] = useState(true);

  //STATE UNTUK DRAGGABLE
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  //LOAD POSISI DARI LOCALSTORAGE
  useEffect(() => {
    const savedPosition = localStorage.getItem('connectionStatusPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        setPosition(parsed);
      } catch (e) {
        console.warn('Gagal load posisi:', e);
      }
    }
  }, []);

  //SAVE POSISI KE LOCALSTORAGE
  const savePosition = (x, y) => {
    const newPos = { x, y };
    setPosition(newPos);
    localStorage.setItem('connectionStatusPosition', JSON.stringify(newPos));
  };

  //RESET POSISI KE DEFAULT
  const resetPosition = () => {
    const defaultX = window.innerWidth - 220;
    const defaultY = window.innerHeight - 80;
    savePosition(defaultX, defaultY);
    
    toast.success('📍 Posisi status ke default', {
      duration: 3000,
      position: 'bottom-right',
    });
  };

  //HANDLE MOUSE DOWN (MULAI DRAG)
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  //HANDLE MOUSE MOVE (DRAG)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      let newX = e.clientX - dragOffset.x;
      let newY = e.clientY - dragOffset.y;
      
      const maxX = window.innerWidth - 220;
      const maxY = window.innerHeight - 80;
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
      
      savePosition(newX, newY);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // ... semua useEffect TETAP SAMA
  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!status.checking && status.online !== prevOnline) {
      if (status.online) {
        toast.success('✅ Connection Established! Online on Supabase', {
          duration: 4000,
          position: 'bottom-right',
        });
      } else {
        toast.error('⚠️ disconnect! Data on LocalStorage', {
          duration: 4000,
          position: 'bottom-right',
        });
      }
      setPrevOnline(status.online);
    }
  }, [status.online, status.checking]);

  useEffect(() => {
    const handleOnline = () => {
      toast.success('✅ Internet Online!', {
        duration: 3000,
        position: 'bottom-right',
      });
      checkStatus();
    };
    const handleOffline = () => {
      toast.error('⚠️ Internet Down! Data disimpan local save mode.', {
        duration: 3000,
        position: 'bottom-right',
      });
      setStatus(prev => ({ ...prev, online: false, mode: 'local' }));
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkStatus = async () => {
    try {
      const result = await checkConnection();
      setStatus({
        online: result.online,
        mode: result.online ? 'supabase' : 'local',
        checking: false
      });
    } catch (error) {
      setStatus({
        online: false,
        mode: 'local',
        checking: false
      });
    }
  };

  const getStatusColor = () => {
    if (status.checking) return 'bg-gray-400';
    return status.online ? 'bg-green-500' : 'bg-red-500';
  };

  const getStatusText = () => {
    if (status.checking) return '⏳ Checking...';
    return status.online ? '✅ Online ' : '⚠️ Offline (LocalStorage)';
  };

  const getStatusIcon = () => {
    if (status.checking) return <FiCloud className="animate-pulse" />;
    return status.online ? <FiWifi /> : <FiWifiOff />;
  };

  return (
    <>
      <Toaster 
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
          },
        }}
      />
      
      {/* DRAGGABLE STATUS BADGE */}
      <div 
        ref={containerRef}
        className={`fixed z-50 cursor-grab select-none transition-shadow duration-200 ${
          isDragging ? 'cursor-grabbing shadow-2xl scale-105' : 'hover:shadow-lg'
        }`}
        style={{
          left: position.x,
          top: position.y,
          transition: isDragging ? 'none' : 'all 0.2s ease',
        }}
        onMouseDown={handleMouseDown}
      >
        <div 
          className={`flex items-center space-x-2 px-3 py-2 rounded-lg shadow-lg border ${
            status.online 
              ? 'bg-green-50/80 dark:bg-green-900/40 border-green-200 dark:border-green-800' 
              : 'bg-red-50/80 dark:bg-red-900/40 border-red-200 dark:border-red-800'
          } backdrop-blur-sm`}
        >
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${getStatusColor()} animate-pulse`} />
            <span className={`text-sm font-medium ${
              status.online 
                ? 'text-green-700 dark:text-green-300' 
                : 'text-red-700 dark:text-red-300'
            }`}>
              {getStatusIcon()}
            </span>
            <span className={`text-xs ${
              status.online 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-red-600 dark:text-red-400'
            }`}>
              {getStatusText()}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
              <FiDatabase className="inline mr-1" />
              {status.mode === 'supabase' ? 'Cloud' : 'Local'}
            </span>
          </div>
          
          {/* TOMBOL REFRESH */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              checkStatus();
            }}
            className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            title="Refresh status"
          >
            <FiRefreshCw size={14} />
          </button>

          {/* TOMBOL RESET POSISI */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              resetPosition();
            }}
            className="text-xs text-gray-400 hover:text-amber-500 dark:text-gray-500 dark:hover:text-amber-400 transition-colors ml-0.5"
            title="Reset posisi ke default (double click)"
          >
            <FiRotateCcw size={13} />
          </button>
        </div>
      </div>
    </>
  );
};

export default ConnectionStatus;